import { Fragment, useState } from "react";
import {
  Check,
  ChevronRight,
  Info,
  MousePointerClick,
  Package,
} from "lucide-react";

export interface PackBrandOption {
  productIdx: number;
  company: string;
  isNoBrand?: boolean;
  notInDt?: boolean;
}

export interface PackPriceOption {
  productIdx: number;
  packSize: number;
  packDt: number;
  packCount: number;
  lineDt: number;
  lineNet: number;
  inDrugTariff: boolean;
  roundedFrom?: number;
  brands: PackBrandOption[];
}

interface Props {
  itemId: string;
  options: PackPriceOption[];
  dispensedQty: number;
  form: string;
  selectedProductIdx?: number;
  onSelect: (productIdx: number) => void;
  onRoundUp?: () => void;
  onKeepExactPacks?: () => void;
  dark: boolean;
}

// get-item-info returns concrete products, so a claim is always made against
// one of them — there is no "any brand" product. "Any Brand" therefore stands
// for the pack size's default product: the cheapest to purchase among the
// brands that reimburse the same, which is the order packPriceOptionsFor
// already puts them in.
const anyBrandIdxFor = (o: PackPriceOption): number =>
  o.brands[0]?.productIdx ?? o.productIdx;

const fmtPackCount = (n: number): string =>
  Number.isInteger(n) ? String(n) : n.toFixed(2);

export function PackSizePriceTable({
  itemId,
  options,
  dispensedQty,
  form,
  selectedProductIdx,
  onSelect,
  onRoundUp,
  onKeepExactPacks,
  dark,
}: Props) {
  const [expandedPacks, setExpandedPacks] = useState<Set<number>>(new Set());
  if (options.length === 0) return null;

  const holdsSelection = (o: PackPriceOption) =>
    o.brands.some((b) => b.productIdx === selectedProductIdx) ||
    o.productIdx === selectedProductIdx;
  const current = options.find(holdsSelection) ?? options[0];

  const muted = dark ? "text-gray-400" : "text-gray-500";
  const faint = dark ? "text-gray-500" : "text-gray-400";
  const selectedRow = dark ? "bg-blue-500/15" : "bg-blue-50";
  const brandRow = dark ? "bg-gray-900/40" : "bg-gray-50/70";
  const guide = dark ? "border-gray-700" : "border-gray-200";
  const radio = "h-3 w-3 accent-blue-600 cursor-pointer";
  const radioName = `claim-pack-${itemId}`;

  const toggleExpanded = (packSize: number) =>
    setExpandedPacks((prev) => {
      const next = new Set(prev);
      if (next.has(packSize)) next.delete(packSize);
      else next.add(packSize);
      return next;
    });

  const claimingBadge = (label: string, title?: string) => (
    <span
      title={title}
      className={`inline-flex items-center gap-0.5 whitespace-nowrap text-[9px] font-semibold px-1.5 py-0.5 rounded ${
        dark ? "bg-blue-500/20 text-blue-300" : "bg-blue-100 text-blue-700"
      }`}
    >
      <Check className="w-2.5 h-2.5" /> {label}
    </span>
  );
  const notInDtChip = (
    <span
      title="Not listed in the Drug Tariff, so it reimburses £0.00"
      className={`whitespace-nowrap text-[9px] font-semibold px-1.5 py-px rounded ${
        dark ? "bg-amber-500/15 text-amber-400" : "bg-amber-100 text-amber-700"
      }`}
    >
      Not in DT
    </span>
  );
  const claimThisHint = (
    <span
      className={`text-[9px] font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity ${
        dark ? "text-blue-300" : "text-blue-600"
      }`}
    >
      Claim this →
    </span>
  );

  return (
    <div
      className={`mt-3 rounded-lg border p-3 ${
        dark ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-white"
      }`}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Package className={`w-3.5 h-3.5 ${muted}`} />
        <span
          className={`text-[10px] font-semibold uppercase tracking-wider ${muted}`}
        >
          Pack sizes &amp; Drug Tariff prices
        </span>
      </div>
      <p className={`text-[10px] mb-2 flex items-center gap-1 ${faint}`}>
        <MousePointerClick className="w-3 h-3 shrink-0" />
        Click a pack size to claim it with Any Brand, or expand it to choose a
        brand. Prices are for the {dispensedQty} {form} dispensed.
      </p>

      <div
        className={`overflow-hidden rounded-md border ${
          dark ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <table className="w-full text-[11px]">
          <thead className={dark ? "bg-gray-900/60" : "bg-gray-50"}>
            <tr
              className={`text-left text-[9px] uppercase tracking-wider ${muted}`}
            >
              <th className="px-2.5 py-1.5 font-semibold">Pack size</th>
              <th className="px-2.5 py-1.5 font-semibold text-right">
                DT price / pack
              </th>
              <th className="px-2.5 py-1.5 font-semibold text-right">Packs</th>
              <th className="px-2.5 py-1.5 font-semibold text-right">
                Line DT
              </th>
              <th className="px-2.5 py-1.5 font-semibold text-right">
                Net reimb
              </th>
              <th className="px-2.5 py-1.5 w-24">
                <span className="sr-only">Claim</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {options.map((o, i) => {
              const expanded = expandedPacks.has(o.packSize);
              const packClaimed = o === current;
              const anyBrandIdx = anyBrandIdxFor(o);
              const anyBrandClaimed =
                packClaimed && selectedProductIdx === anyBrandIdx;
              const claimedBrand = packClaimed
                ? o.brands.find((b) => b.productIdx === selectedProductIdx)
                : undefined;
              const onlyNoBrand =
                o.brands.length > 0 && o.brands.every((b) => b.isNoBrand);
              const defaultBrandLabel = onlyNoBrand ? "No Brand" : "Any Brand";
              return (
                <Fragment key={o.packSize}>
                  <tr
                    onClick={() => {
                      if (!packClaimed) onSelect(anyBrandIdx);
                    }}
                    title={
                      packClaimed
                        ? "Currently claimed"
                        : `Click to claim ${o.packSize} ${form} — ${defaultBrandLabel}`
                    }
                    className={`group transition-colors ${
                      i > 0
                        ? `border-t ${
                            dark ? "border-gray-700/60" : "border-gray-100"
                          }`
                        : ""
                    } ${
                      packClaimed
                        ? selectedRow
                        : `cursor-pointer ${
                            dark ? "hover:bg-gray-800" : "hover:bg-gray-50"
                          }`
                    }`}
                  >
                    <td
                      className={`px-2.5 py-1.5 ${
                        dark ? "text-gray-100" : "text-gray-800"
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(o.packSize);
                          }}
                          aria-expanded={expanded}
                          aria-label={`${
                            expanded ? "Hide" : "Show"
                          } brands for ${o.packSize} ${form}`}
                          className={`shrink-0 rounded p-0.5 cursor-pointer transition-colors ${
                            dark
                              ? "text-gray-400 hover:bg-gray-700 hover:text-gray-200"
                              : "text-gray-400 hover:bg-gray-200/70 hover:text-gray-700"
                          }`}
                        >
                          <ChevronRight
                            className={`w-3 h-3 transition-transform ${
                              expanded ? "rotate-90" : ""
                            }`}
                          />
                        </button>
                        <label
                          className="flex flex-wrap items-center gap-1.5 cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="radio"
                            name={radioName}
                            checked={packClaimed}
                            onChange={() => {
                              if (!packClaimed) onSelect(anyBrandIdx);
                            }}
                            aria-label={`Claim against ${o.packSize} ${form}, ${
                              packClaimed && claimedBrand
                                ? claimedBrand.company
                                : defaultBrandLabel
                            }`}
                            className={radio}
                          />
                          <span className="font-medium">
                            {o.packSize} {form}
                          </span>
                          {!o.inDrugTariff && notInDtChip}
                          {/* The claimed pack names its brand here so the pack
                              size being claimed is readable without expanding. */}
                          <span
                            title={
                              packClaimed && claimedBrand
                                ? `Claiming ${claimedBrand.company}`
                                : undefined
                            }
                            className={`max-w-[160px] truncate text-[9px] font-medium px-1.5 py-px rounded ${
                              packClaimed
                                ? dark
                                  ? "bg-blue-500/20 text-blue-300"
                                  : "bg-blue-100 text-blue-700"
                                : dark
                                ? "bg-gray-700 text-gray-300"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {packClaimed && claimedBrand && !anyBrandClaimed
                              ? claimedBrand.company
                              : defaultBrandLabel}
                          </span>
                        </label>
                      </div>
                    </td>
                    <td
                      className={`px-2.5 py-1.5 text-right tabular-nums ${
                        dark ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      £{o.packDt.toFixed(2)}
                    </td>
                    <td
                      className={`px-2.5 py-1.5 text-right tabular-nums ${muted}`}
                    >
                      {fmtPackCount(o.packCount)}
                      {packClaimed && o.roundedFrom != null && (
                        <span
                          className={`block text-[9px] whitespace-nowrap ${faint}`}
                        >
                          from {fmtPackCount(o.roundedFrom)}
                          {onKeepExactPacks && (
                            <>
                              {" · "}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onKeepExactPacks();
                                }}
                                className={`cursor-pointer underline ${
                                  dark ? "text-blue-300" : "text-blue-600"
                                }`}
                              >
                                Undo
                              </button>
                            </>
                          )}
                        </span>
                      )}
                      {packClaimed &&
                        o.roundedFrom == null &&
                        onRoundUp &&
                        !Number.isInteger(o.packCount) && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRoundUp();
                            }}
                            className={`block ml-auto text-[9px] font-semibold whitespace-nowrap cursor-pointer ${
                              dark
                                ? "text-blue-300 hover:text-blue-200"
                                : "text-blue-600 hover:text-blue-700"
                            }`}
                          >
                            Round up to {Math.ceil(o.packCount)}
                          </button>
                        )}
                    </td>
                    <td
                      className={`px-2.5 py-1.5 text-right tabular-nums ${
                        dark ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      £{o.lineDt.toFixed(2)}
                    </td>
                    <td className="px-2.5 py-1.5 text-right tabular-nums">
                      <span
                        className={`font-semibold ${
                          dark ? "text-gray-100" : "text-gray-900"
                        }`}
                      >
                        £{o.lineNet.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-2.5 py-1.5 text-right">
                      {packClaimed
                        ? claimingBadge(
                            "Claiming",
                            `Claiming ${
                              o.packSize
                            } ${form} — ${claimedBrand?.company ??
                              defaultBrandLabel}`,
                          )
                        : claimThisHint}
                    </td>
                  </tr>

                  {expanded && (
                    <>
                      <tr className={brandRow}>
                        <td colSpan={6} className="px-2.5 py-0">
                          <p
                            className={`ml-[27px] border-l pl-3 pt-1.5 pb-0.5 flex items-center gap-1 text-[9px] ${guide} ${faint}`}
                          >
                            <Info className="w-2.5 h-2.5 shrink-0" />
                            {o.brands.length === 0
                              ? "No alternative brands are listed for this pack size."
                              : o.brands.every((b) => b.notInDt)
                              ? "Not listed in the Drug Tariff, so this pack size reimburses £0.00."
                              : o.brands.some((b) => b.notInDt)
                              ? "Brands marked Not in DT aren't listed in the Drug Tariff and reimburse £0.00."
                              : `Brand doesn't change the Drug Tariff price: £${o.packDt.toFixed(
                                  2,
                                )} per pack.`}
                          </p>
                        </td>
                      </tr>
                      {o.brands.map((brand) => {
                        const claimed = selectedProductIdx === brand.productIdx;
                        return (
                          <tr
                            key={brand.productIdx}
                            onClick={() => {
                              if (!claimed) onSelect(brand.productIdx);
                            }}
                            title={
                              claimed
                                ? "Currently claimed"
                                : `Click to claim ${o.packSize} ${form} — ${brand.company}`
                            }
                            className={`group transition-colors ${
                              claimed
                                ? selectedRow
                                : `cursor-pointer ${brandRow} ${
                                    dark
                                      ? "hover:bg-gray-800"
                                      : "hover:bg-gray-100"
                                  }`
                            }`}
                          >
                            <td colSpan={5} className="px-2.5 py-0">
                              <label
                                className={`ml-[27px] border-l pl-3 py-1.5 flex items-center gap-1.5 cursor-pointer ${guide} ${
                                  dark ? "text-gray-200" : "text-gray-700"
                                }`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="radio"
                                  name={radioName}
                                  checked={claimed}
                                  onChange={() => onSelect(brand.productIdx)}
                                  aria-label={`Claim against ${o.packSize} ${form}, ${brand.company}`}
                                  className={radio}
                                />
                                <span
                                  className={
                                    claimed ? "font-medium" : undefined
                                  }
                                >
                                  {brand.company}
                                </span>
                                {brand.notInDt && notInDtChip}
                              </label>
                            </td>
                            <td className="px-2.5 py-1.5 text-right">
                              {claimed
                                ? claimingBadge("Claiming")
                                : claimThisHint}
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
